import { createSyncStoragePersister } from "@tanstack/query-sync-storage-persister";
import { QueryClient } from "@tanstack/react-query";
import { PersistQueryClientProvider } from "@tanstack/react-query-persist-client";
import { Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import Index from "./pages/Index";
import LandingPage from "./pages/LandingPage";

import { AuthGuard } from "@/components/AuthGuard";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { OfflineBanner } from "@/components/OfflineBanner";
import { OfflineSyncToast } from "@/components/OfflineSyncToast";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AuthProvider } from "@/contexts/AuthContext";
import { NetworkStatusProvider } from "@/contexts/NetworkStatusContext";
import { UserSettingsProvider } from "@/contexts/UserSettingsContext";

import { supabase } from "@/integrations/supabase/supabaseClient";
import { logMetric, startAutoFlush } from "@/perf/collector";
import DevPerfDashboard from "@/perf/DevPerfDashboard";
import { lazyWithPerf } from "@/perf/lazyWithPerf";

import { repositoryFactory } from "@/repositories/RepositoryFactory";

const AllSubscriptions = lazyWithPerf(
  () => import("./pages/AllSubscriptions"),
  "AllSubscriptions",
);
const Calendar = lazyWithPerf(() => import("./pages/Calendar"), "Calendar");
const Notifications = lazyWithPerf(
  () => import("./pages/Notifications"),
  "Notifications",
);
const Auth = lazyWithPerf(() => import("./pages/Auth"), "Auth");
const ResetPassword = lazyWithPerf(
  () => import("./pages/ResetPassword"),
  "ResetPassword",
);
const NotFound = lazyWithPerf(() => import("./pages/NotFound"), "NotFound");
const SettingsLayout = lazyWithPerf(
  () => import("./pages/settings/Layout"),
  "SettingsLayout",
);
const SettingsGeneral = lazyWithPerf(
  () => import("./pages/settings/General"),
  "SettingsGeneral",
);
const SettingsPreferences = lazyWithPerf(
  () => import("./pages/settings/Preferences"),
  "SettingsPreferences",
);
const SettingsData = lazyWithPerf(
  () => import("./pages/settings/Data"),
  "SettingsData",
);
const SettingsIntegrations = lazyWithPerf(
  () => import("./pages/settings/Integrations"),
  "SettingsIntegrations",
);
const SettingsSystem = lazyWithPerf(
  () => import("./pages/settings/System"),
  "SettingsSystem",
);
const SettingsFeedback = lazyWithPerf(
  () => import("./pages/settings/Feedback"),
  "SettingsFeedback",
);
const SettingsNotifications = lazyWithPerf(
  () => import("./pages/settings/Notifications"),
  "SettingsNotifications",
);
const SettingsBilling = lazyWithPerf(
  () => import("./pages/settings/Billing"),
  "SettingsBilling",
);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 min — serve cached data without refetch
      gcTime: 24 * 60 * 60 * 1000, // 24 hr — keep data available for offline
    },
    mutations: {
      retry: 3,
    },
  },
});

// ── Register default mutation functions for offline persistence ──────────
// When paused mutations survive a page reload, the mutationFn closure is lost.
// Registering defaults ensures the function is available after rehydration.

const subRepo = repositoryFactory.getSubscriptionRepository();
const catRepo = repositoryFactory.getCategoryRepository();
const payRepo = repositoryFactory.getPaymentHistoryRepository();
const lifecycleRepo = repositoryFactory.getLifecycleEventRepository();
const periodRepo = repositoryFactory.getSubscriptionPeriodRepository();

queryClient.setMutationDefaults(["createSubscription"], {
  mutationFn: (data: any) => subRepo.create(data),
});
queryClient.setMutationDefaults(["updateSubscription"], {
  mutationFn: ({ id, ...updates }: any) => subRepo.update(id, updates),
});
queryClient.setMutationDefaults(["deleteSubscription"], {
  mutationFn: (id: string) => subRepo.delete(id),
});

queryClient.setMutationDefaults(["createCategory"], {
  mutationFn: (data: any) => catRepo.create(data),
});
queryClient.setMutationDefaults(["updateCategory"], {
  mutationFn: ({ id, ...updates }: any) => catRepo.update(id, updates),
});
queryClient.setMutationDefaults(["deleteCategory"], {
  mutationFn: (id: string) => catRepo.delete(id),
});

queryClient.setMutationDefaults(["createPayment"], {
  mutationFn: (data: any) => payRepo.create(data),
});
queryClient.setMutationDefaults(["deletePayment"], {
  mutationFn: (id: string) => payRepo.delete(id),
});

queryClient.setMutationDefaults(["createLifecycleEvent"], {
  mutationFn: (data: any) => lifecycleRepo.create(data),
});
queryClient.setMutationDefaults(["updateLifecycleEvent"], {
  mutationFn: ({ id, ...updates }: any) => lifecycleRepo.update(id, updates),
});
queryClient.setMutationDefaults(["deleteLifecycleEvent"], {
  mutationFn: (id: string) => lifecycleRepo.delete(id),
});

queryClient.setMutationDefaults(["createSubscriptionPeriod"], {
  mutationFn: (data: any) => periodRepo.create(data),
});
queryClient.setMutationDefaults(["updateSubscriptionPeriod"], {
  mutationFn: ({ id, ...updates }: any) => periodRepo.update(id, updates),
});
queryClient.setMutationDefaults(["deleteSubscriptionPeriod"], {
  mutationFn: (id: string) => periodRepo.delete(id),
});

queryClient.setMutationDefaults(["addSubscriptionTag"], {
  mutationFn: async (params: {
    subscriptionId: string;
    categoryId: string;
  }) => {
    const { data, error } = await supabase
      .from("subscription_tags")
      .insert({
        user_id: (await supabase.auth.getUser()).data.user!.id,
        subscription_id: params.subscriptionId,
        category_id: params.categoryId,
      })
      .select()
      .single();
    if (error) throw error;
    return data;
  },
});
queryClient.setMutationDefaults(["removeSubscriptionTag"], {
  mutationFn: async (params: {
    subscriptionId: string;
    categoryId: string;
  }) => {
    const { error } = await supabase
      .from("subscription_tags")
      .delete()
      .eq("subscription_id", params.subscriptionId)
      .eq("category_id", params.categoryId);
    if (error) throw error;
  },
});

// ── Persist React Query cache to localStorage ────────────────────────────
const persister = createSyncStoragePersister({
  storage: window.localStorage,
});

// ── Perf monitoring ──────────────────────────────────────────────────────
startAutoFlush();

try {
  const cache = queryClient.getQueryCache();
  cache.subscribe((event) => {
    const q = (event as any).query;
    if (!q) return;

    try {
      const fs = q.state?.fetchStatus;

      if (fs === "fetching") {
        if (!q.meta) q.meta = {};
        if (!q.meta.__perfStart) q.meta.__perfStart = Date.now();
        return;
      }

      if (fs === "idle" && q.meta && q.meta.__perfStart) {
        const start = q.meta.__perfStart;
        delete q.meta.__perfStart;
        const duration = Date.now() - start;
        const key = Array.isArray(q.queryKey)
          ? q.queryKey.join(",")
          : String(q.queryKey);
        const status = q.state?.status;
        const data = q.state?.data;

        logMetric({
          type: "react-query",
          name: key,
          value: typeof duration === "number" ? duration : undefined,
          timestamp: Date.now(),
          extra: {
            status,
            resultCount: Array.isArray(data) ? data.length : undefined,
            fetchStatus: fs,
          },
        });
      }
    } catch {
      // best-effort; swallow telemetry errors
    }
  });
} catch {
  // best-effort subscription; ignore failures
}

const App = () => (
  <PersistQueryClientProvider
    client={queryClient}
    persistOptions={{
      persister,
      maxAge: 24 * 60 * 60 * 1000, // 24hr, matches gcTime
      buster: import.meta.env.VITE_BUILD_HASH || "v1",
    }}
    onSuccess={() => {
      void queryClient.resumePausedMutations().then(() => {
        void queryClient.invalidateQueries();
      });
    }}
  >
    <NetworkStatusProvider>
      <ErrorBoundary>
        <AuthProvider>
          <UserSettingsProvider>
            <TooltipProvider>
              <Sonner />
              <BrowserRouter>
                <OfflineBanner />
                <OfflineSyncToast />
                <Routes>
                  <Route
                    path="/auth"
                    element={
                      <Suspense fallback={<div />}>
                        <Auth />
                      </Suspense>
                    }
                  />
                  <Route
                    path="/auth/reset-password"
                    element={
                      <Suspense fallback={<div />}>
                        <ResetPassword />
                      </Suspense>
                    }
                  />
                  {/* Public landing page — no auth required */}
                  <Route path="/" element={<LandingPage />} />
                  {/* Authenticated app — dashboard */}
                  <Route
                    path="/app"
                    element={
                      <AuthGuard>
                        <Index />
                      </AuthGuard>
                    }
                  />
                  <Route
                    path="/subscriptions"
                    element={
                      <AuthGuard>
                        <Suspense fallback={<div />}>
                          <AllSubscriptions />
                        </Suspense>
                      </AuthGuard>
                    }
                  />
                  <Route
                    path="/calendar"
                    element={
                      <AuthGuard>
                        <Suspense fallback={<div />}>
                          <Calendar />
                        </Suspense>
                      </AuthGuard>
                    }
                  />
                  <Route
                    path="/notifications"
                    element={
                      <AuthGuard>
                        <Suspense fallback={<div />}>
                          <Notifications />
                        </Suspense>
                      </AuthGuard>
                    }
                  />
                  <Route
                    path="/settings"
                    element={
                      <AuthGuard>
                        <Suspense fallback={<div />}>
                          <SettingsLayout />
                        </Suspense>
                      </AuthGuard>
                    }
                  >
                    <Route index element={<Navigate to="general" replace />} />
                    <Route
                      path="general"
                      element={
                        <Suspense fallback={<div />}>
                          <SettingsGeneral />
                        </Suspense>
                      }
                    />
                    <Route
                      path="preferences"
                      element={
                        <Suspense fallback={<div />}>
                          <SettingsPreferences />
                        </Suspense>
                      }
                    />
                    <Route
                      path="integrations"
                      element={
                        <Suspense fallback={<div />}>
                          <SettingsIntegrations />
                        </Suspense>
                      }
                    />
                    <Route
                      path="data"
                      element={
                        <Suspense fallback={<div />}>
                          <SettingsData />
                        </Suspense>
                      }
                    />
                    <Route
                      path="system"
                      element={
                        <Suspense fallback={<div />}>
                          <SettingsSystem />
                        </Suspense>
                      }
                    />
                    <Route
                      path="feedback"
                      element={
                        <Suspense fallback={<div />}>
                          <SettingsFeedback />
                        </Suspense>
                      }
                    />
                    <Route
                      path="notifications"
                      element={
                        <Suspense fallback={<div />}>
                          <SettingsNotifications />
                        </Suspense>
                      }
                    />
                    <Route
                      path="billing"
                      element={
                        <Suspense fallback={<div />}>
                          <SettingsBilling />
                        </Suspense>
                      }
                    />
                  </Route>
                  {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                  <Route
                    path="*"
                    element={
                      <Suspense fallback={<div />}>
                        <NotFound />
                      </Suspense>
                    }
                  />
                </Routes>
              </BrowserRouter>
              {import.meta.env.DEV && <DevPerfDashboard />}
            </TooltipProvider>
          </UserSettingsProvider>
        </AuthProvider>
      </ErrorBoundary>
    </NetworkStatusProvider>
  </PersistQueryClientProvider>
);

export default App;
